import React, { useCallback, useMemo, useRef, useState } from 'react';
import T from 'prop-types';
import { get } from 'lodash-es';

import Modal from '../../../../components/Modal';
import Button from '../../../../components/Buttons/Button';
import MultiLevelSelectTypeahead from '../../../../components/MultiLevelSelectTypeahead';

import api from '../../api';
import { initParams } from '../../configs';

const { currentUser } = initParams;

const emptyState = (
  <div className="d-flex align-items-center justify-content-center py-sm">
    <div className="px-sm mb-sm">
      <p>No users match your search criteria.</p>
    </div>
  </div>
);

const renderSelectedUsers = (option, _, onItemRemove) => {
  const fullName = option.label.split(/\(.+\)/)[0].trim();

  return (
    <div key={option.id} className="selected-options__item">
      <span className="selected-options__item-label">{fullName || option.value}</span>
      <span
        className="selected-options__item-remove selected-options__item-remove--link"
        onClick={() => {
          onItemRemove(option);
        }}
      >
        Remove
      </span>
    </div>
  );
};

const ModalSharedReport = ({ open, onClose, onShareSuccess, report }) => {
  const [processing, setProcessing] = useState(false);
  const [users, setUsers] = useState([]);
  const [textLetter, setTextLetter] = useState(
    `Check out this research report from Apptopia. Let me know if you found it valuable. — ${currentUser.full_name}`
  );
  const [errorMessage, setErrorMessage] = useState(null);

  const usersEmails = useMemo(() => users.map(({ value }) => value), [users]);
  const selectTypeaheadRef = useRef();

  const handleChangeUsers = useCallback((value) => {
    const typeaheadInputRef = get(selectTypeaheadRef, ['current', 'searchInputRef', 'current']);
    let shouldTriggerSearch = false;

    setUsers((prevUsers) => {
      if (value.length > prevUsers.length) shouldTriggerSearch = true;
      return value;
    });
    setErrorMessage(null);

    // Workaround, can be removed after refactor MultiLevelSelectTypeahead
    if (shouldTriggerSearch && typeaheadInputRef) {
      setTimeout(() => {
        typeaheadInputRef.blur();
        typeaheadInputRef.focus();
      }, 10);
    }
  }, []);

  const handleModalClose = () => {
    if (!processing) {
      onClose(false);
    }
  };

  const handleUserRemove = useCallback(({ value }) => {
    setUsers((prevUsers) => prevUsers.filter((user) => user.value !== value));
    setErrorMessage(null);
  }, []);

  const handleResponseList = useCallback(
    (usersList) => {
      const skipEmails = [...usersEmails];

      return usersList.filter(({ value }) => !skipEmails.includes(value));
    },
    [usersEmails]
  );

  const submitDisabled = processing;
  const handleSubmit = () => {
    if (submitDisabled) return;

    setProcessing(true);
    api.marketReports
      .shareReport({
        emails: usersEmails,
        body: textLetter,
        iteration_id: report.id,
      })
      .then(() => {
        onShareSuccess(true, usersEmails.length);
        setProcessing(false);
        onClose(false);
        setErrorMessage(null);
      })
      .catch((error) => {
        if (error.response.data.length > 0) {
          setErrorMessage(error.response.data.error);
        } else {
          setErrorMessage('An error occurred!');
        }
        setProcessing(false);
      });
  };

  const handleTextLetterChange = ({ target: { value } }) => {
    if (textLetter.length >= 200) {
      setTextLetter(value.slice(0, 200));
    } else {
      setTextLetter(value);
    }
  };

  return (
    <Modal
      size="xs"
      open={open}
      modalClassName="modal-compare-shared my-research-share-modal"
      closeButtonIconClass="hicon-close text-16 text-black"
      onClose={handleModalClose}
    >
      <div className="modal-compare-shared-header">
        <h1 className="modal-compare-shared-title">Share Report</h1>
      </div>
      <div className="modal-compare-shared-body">
        <p className="modal-compare-shared-description">
          You may share this report with up anyone in your organization. People outside of your
          organization will not be able to view the report.
        </p>
        <div className="personal-search">
          <label>Recipients</label>
          <MultiLevelSelectTypeahead
            ref={selectTypeaheadRef}
            emptyState={emptyState}
            selection={users}
            dropdownHeight="190px;"
            requestPath="/users?allowed_blocked=true"
            placeholder="Search users"
            childrenContainerWidth="100%"
            childrenContainerHeight="134px"
            clearable
            clearOnSelect
            selectId="users-search"
            selectedOptionsClassName="d-flex flex-wrap justify-content-center"
            multi
            inlineSelected
            requestNewUserState="addNewUser"
            responseDecorator={handleResponseList}
            childrenForm={renderSelectedUsers}
            onItemRemove={handleUserRemove}
            onChange={handleChangeUsers}
          />
          {errorMessage && <div className="error-message text-red">{errorMessage}</div>}
        </div>
        <label className="mt-lg">Message</label>
        <textarea
          className="form-control"
          rows="4"
          value={textLetter}
          onChange={handleTextLetterChange}
        />
        <p className="text-right">{textLetter.length}/200</p>
      </div>

      <div className="modal-compare-shared-footer justify-content-end">
        <Button variant="secondary" className="mr-xs" onClick={handleModalClose}>
          Cancel
        </Button>
        <Button disabled={submitDisabled} loading={processing} onClick={handleSubmit}>
          Share Report
        </Button>
      </div>
    </Modal>
  );
};

ModalSharedReport.propTypes = {
  report: T.shape({
    id: T.number,
  }).isRequired,
  open: T.bool.isRequired,
  onClose: T.func.isRequired,
  onShareSuccess: T.func.isRequired,
};

export default ModalSharedReport;
